import React from "react"
import { Form, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileLines } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useDashboard } from "features/Reporting/hooks/Dashboard/useDashboard"
import { Project } from "stores/projectStore"
import { AxiosResponse } from "axios"

// Form values interface
interface ReportFormValues {
  name: string
}

// Props interface for the component
interface CreateReportFormProps {
  /** Callback function to handle report creation response */
  createReportCallback: (
    res: AxiosResponse<unknown>,
    reportName: string
  ) => void
  /** Function to show/hide the modal */
  setIsModalVisible: (isVisible: boolean) => void
  /** Project object containing project details */
  project: Project
}

/**
 * CreateReportForm component renders a form for creating a new report.
 *
 * @component
 * @example
 * const createReportCallback = (res, reportName) => {
 *   console.log(res, reportName);
 * };
 * const setIsModalVisible = (isVisible) => {
 *   // logic to show/hide modal
 * };
 * return (
 *   <CreateReportForm
 *     createReportCallback={createReportCallback}
 *     setIsModalVisible={setIsModalVisible}
 *     project={project}
 *   />
 * );
 */
const CreateReportForm: React.FC<CreateReportFormProps> = ({
  createReportCallback,
  setIsModalVisible,
  project,
}) => {
  const [form] = Form.useForm<ReportFormValues>()
  const { saveDashboardFile, isSaving } = useDashboard()

  /**
   * Submits the report form and handles the response.
   *
   * @async
   * @function
   * @param reportFormValues - The values from the report form
   */
  const submitReportForm = async (
    reportFormValues: ReportFormValues
  ): Promise<void> => {
    try {
      const initialContent: string = ""

      const res = await saveDashboardFile(
        project.id,
        project.Created_by,
        initialContent,
        reportFormValues.name
      )

      createReportCallback(res, reportFormValues.name)
      hideModal()
    } catch (error) {
      console.error("Error creating report:", error)
    }
  }

  /**
   * Resets the form fields and hides the modal.
   */
  const hideModal = (): void => {
    form.resetFields()
    setIsModalVisible(false)
  }

  return (
    <Form
      form={form}
      name="create-report-form"
      onFinish={submitReportForm}
      style={{ textAlign: "left" }}
      layout="vertical"
    >
      <Form.Item
        style={{ marginBottom: "2em" }}
        name="name"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please Enter a Name",
          },
        ]}
      >
        <Input
          prefix={
            <FontAwesomeIcon
              className="flex-shrink-0 h-5 mr-2"
              icon={faFileLines}
            />
          }
          placeholder="Dashboard Name"
        />
      </Form.Item>

      <div className="flex flex-row mt-5">
        <div className="ml-auto mr-2 space-x-2">
          <DataQButton type="primary" htmlType="submit" isLoading={isSaving}>
            Create Dashboard
          </DataQButton>
          <DataQButton onClick={hideModal} type="secondary">
            Cancel
          </DataQButton>
        </div>
      </div>
    </Form>
  )
}

export default CreateReportForm
